/**
 * @file Various utility functions
 * @author Tobias Braner
 */

/**
 * Used in `formatNumber`, determined by the lang attribute of the html DOM node.
 */
const locale = document.documentElement.getAttribute('lang')

/**
 * Formats a number using the `toLocaleString` as decimal with or without separator considering the current language
 * set to the HTML element `const locale = document.documentElement.getAttribute('lang')`;
 * @param {number} num The number to format
 * @param {number} decs The number of decimals
 * @returns {string} The formatted numeric string
 */
export function formatNumber(num, decs = 0) {
  return num.toLocaleString(locale, { maximumFractionDigits: decs });
}

/**
 * Check if `child` argument is a descendant of `parent`, which means something like "Is it really a child?".
 * @param {HTMLElement} parent DOM node
 * @param {HTMLElement} child DOM node
 * @returns {boolean} true if the child is really a child of the parent, else false is returned
 */
export function isDescendant(parent, child) {
  let node = child.parentNode;
  while (node) {
    if (node === parent) {
      return true;
    }

    // Traverse up to the parent
    node = node.parentNode;
  }

  // Go up until the root but couldn't find the `parent`
  return false;
};


/**
 * Debounces a function call. Useful when event emitters like 'scroll' continuously create new events, but you want
 * to wait until the user stops, or takes a small break.
 * @function
 * @param {function} callback The function finally called
 * @param {number} wait Time in ms to wait after the last call before calling the callback function
 * @returns {function} the pointer to the debounced function
 */
export const debounce = (callback, wait) => {
  let timeoutId = null;
  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      callback.apply(null, args);
    }, wait);
  };
}

/**
 * Calls a callback function when the DOM is ready. Usually you want to add all your initialization code into
 * a callback you pass to the ready function.
 * @param {function} callbackFunc The function called when the browser is ready. Modern version of jQueries ready fn.
 */
export function ready(callbackFunc) {
  if (document.readyState !== 'loading') {
    // Document is already ready, call the callback directly
    callbackFunc();
  } else if (document.addEventListener) {
    // All modern browsers to register DOMContentLoaded
    document.addEventListener('DOMContentLoaded', callbackFunc);
  } else {
    // Old IE browsers
    document.attachEvent('onreadystatechange', function () {
      if (document.readyState === 'complete') {
        callbackFunc();
      }
    });
  }
}